/**
 * Chat Store — manages chat messages with local persistence.
 *
 * Messages are persisted on-device via AsyncStorage.
 * No server fetch — clear = gone forever.
 *
 * Delivery tracking: user messages start as 'sending' (single tick),
 * become 'sent' (double tick) when server ACKs, or 'failed' on timeout.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import AsyncStorage from '@react-native-async-storage/async-storage';
import type { ChatMessage, WsMessagePayload } from '../types/api';
import { wsManager, type WsState } from '../ws/WebSocketManager';
import { showReminderNotification, cancelScheduledReminder, cancelHeadsupReminder } from '../utils/notifications';
import { AppState, Platform } from 'react-native';
import { isOnChatScreen } from './navigationStore';
import { useToastStore } from './toastStore';
import { useNotificationHistory } from './notificationHistoryStore';
import * as api from '../api/client';
import { requestOfflineQueueReplay } from './offlineQueueReplay';

/** Web-safe AppState check — on web, AppState may not be available */
const isAppActive = () =>
  Platform.OS === 'web' ? (typeof document !== 'undefined' && !document.hidden) : AppState.currentState === 'active';
import * as Notifications from 'expo-notifications';

const MAX_LOCAL_MESSAGES = 500;

function wsToChat(payload: WsMessagePayload, direction: 'inbound' | 'outbound' = 'outbound'): ChatMessage {
  return {
    id: Date.now() + Math.random(),
    wa_id: '',
    direction,
    channel: 'app',
    msg_type: payload.msgType || 'text',
    body: payload.body || '',
    metadata: payload.buttons || payload.sections
      ? JSON.stringify({
          footer: payload.footer,
          buttons: payload.buttons,
          header: payload.header,
          buttonText: payload.buttonText,
          sections: payload.sections,
        })
      : null,
    created_at_utc: payload.timestamp || new Date().toISOString(),
  };
}

function capMessages(msgs: ChatMessage[]): ChatMessage[] {
  return msgs.length > MAX_LOCAL_MESSAGES ? msgs.slice(0, MAX_LOCAL_MESSAGES) : msgs;
}

/** Update status of a message by clientMsgId */
function updateMsgStatus(msgs: ChatMessage[], msgId: string, status: 'sending' | 'sent' | 'failed'): ChatMessage[] {
  return msgs.map(m => m.clientMsgId === msgId ? { ...m, status } : m);
}

function prependApiMessages(msgs: ChatMessage[], payloads: WsMessagePayload[] = []): ChatMessage[] {
  if (!Array.isArray(payloads) || payloads.length === 0) return msgs;
  const incoming = payloads.map((payload) => wsToChat(payload)).reverse();
  return capMessages([...incoming, ...msgs]);
}

interface ChatState {
  messages: ChatMessage[];
  wsConnected: boolean;
  wsState: WsState;
  isLoadingMore: boolean;
  hasMore: boolean;
  isBotTyping: boolean;

  initWebSocket: () => void;
  cleanupWebSocket: () => void;
  sendMessage: (text: string) => void;
  sendButton: (buttonId: string, buttonLabel?: string) => void;
  sendListSelect: (itemId: string, itemLabel?: string) => void;
  addLocalMessage: (text: string, direction: 'inbound' | 'outbound') => void;
  deleteMessage: (msgId: string | number) => void;
  clearMessages: () => void;
  loadMoreHistory: () => Promise<void>;
  toggleReaction: (messageId: number, emoji: string) => void;
}

let _unsubMessage: (() => void) | null = null;
let _unsubAck: (() => void) | null = null;
let _unsubAckTimeout: (() => void) | null = null;
let _unsubState: (() => void) | null = null;
let _unsubFired: (() => void) | null = null;
let _unsubForceLogout: (() => void) | null = null;

export const useChatStore = create<ChatState>()(
  persist(
    (set, get) => ({
      messages: [],
      wsConnected: false,
      wsState: 'disconnected' as WsState,
      isLoadingMore: false,
      // Chat history is fully local; there is no older server page to fetch.
      hasMore: false,
      isBotTyping: false,

      initWebSocket: () => {
        _unsubMessage?.();
        _unsubAck?.();
        _unsubAckTimeout?.();
        _unsubState?.();
        _unsubFired?.();
        _unsubForceLogout?.();

        // Incoming bot messages
        _unsubMessage = wsManager.on('message', (data) => {
          const msg = wsToChat(data.payload);
          set((state) => ({
            messages: capMessages([msg, ...state.messages]),
            isBotTyping: false,
          }));

          // Notification routing: background → OS notification, foreground non-chat → toast, foreground chat → silent
          const notifBody = msg.body?.substring(0, 200) || 'New message';
          useNotificationHistory.getState().addNotification({ title: 'FishMemory', body: notifBody, type: 'message' });
          if (!isAppActive()) {
            Notifications.scheduleNotificationAsync({
              content: {
                title: 'FishMemory',
                body: notifBody,
                data: { type: 'message' },
              },
              trigger: null,
            });
          } else if (!isOnChatScreen()) {
            useToastStore.getState().showToast(
              'FishMemory',
              notifBody,
            );
          }
        });

        // ACK from server — mark message as delivered
        _unsubAck = wsManager.on('ack', (data) => {
          const msgId = data.payload?.msgId;
          if (!msgId) return;
          set((state) => ({
            messages: updateMsgStatus(state.messages, msgId, 'sent'),
          }));
        });

        // ACK timeout — mark as failed
        _unsubAckTimeout = wsManager.on('ack_timeout', (data) => {
          const msgId = data.payload?.msgId;
          if (!msgId) return;
          set((state) => ({
            messages: updateMsgStatus(state.messages, msgId, 'failed'),
          }));
        });

        // Track connection state
        _unsubState = wsManager.onStateChange((wsState) => {
          set({ wsConnected: wsState === 'connected', wsState });
          // Flush offline mutation queue on reconnect
          if (wsState === 'connected') {
            requestOfflineQueueReplay(0);
          }
        });

        // Reminder fired events
        _unsubFired = wsManager.on('reminder_fired', (data) => {
          const { seq, body, firedAt, buttons: serverButtons } = data.payload || {};
          if (!seq) return;
          const buttons = (serverButtons || []).map((b: { id: string; title: string }) => ({
            id: b.id,
            title: b.title,
          }));
          const reminderMsg: ChatMessage = {
            id: Date.now() + Math.random(),
            wa_id: '',
            direction: 'outbound',
            channel: 'app',
            msg_type: 'interactive_buttons',
            body: body || `🔔 Reminder #${seq}`,
            metadata: JSON.stringify({ buttons }),
            created_at_utc: firedAt || new Date().toISOString(),
          };
          set((state) => ({
            messages: capMessages([reminderMsg, ...state.messages]),
            isBotTyping: false,
          }));

          // Notification routing: background → OS notification, foreground non-chat → toast, foreground chat → cancel scheduled only
          const reminderBody = body || `Reminder #${seq}`;
          useNotificationHistory.getState().addNotification({ title: '⏰ Reminder', body: reminderBody, type: 'reminder' });
          if (!isAppActive()) {
            showReminderNotification(seq, reminderBody, firedAt);
          } else {
            cancelScheduledReminder(seq);
            cancelHeadsupReminder(seq);
            if (!isOnChatScreen()) {
              useToastStore.getState().showToast(
                '⏰ Reminder',
                body || `Reminder #${seq}`,
              );
            }
          }
        });

        // Force logout from another device
        _unsubForceLogout = wsManager.on('force_logout', () => {
          // Clear local state without calling server (server already invalidated tokens)
          wsManager.disconnect();
          import('./authStore').then(({ useAuthStore }) => {
            const auth = useAuthStore.getState();
            import('../utils/storage').then(({ clearAll }) => {
              clearAll().then(() => {
                auth._forceLogout();
              });
            });
          });
        });

        set({ wsConnected: wsManager.isConnected, wsState: wsManager.state });
        if (!wsManager.isConnected) {
          wsManager.connect();
        }
      },

      cleanupWebSocket: () => {
        _unsubMessage?.();
        _unsubAck?.();
        _unsubAckTimeout?.();
        _unsubState?.();
        _unsubFired?.();
        _unsubForceLogout?.();
        _unsubMessage = null;
        _unsubAck = null;
        _unsubAckTimeout = null;
        _unsubState = null;
        _unsubFired = null;
        _unsubForceLogout = null;
      },

      sendMessage: (text: string) => {
        const { sent, msgId } = wsManager.sendChat(text);
        const localMsg: ChatMessage = {
          id: Date.now(),
          wa_id: '',
          direction: 'inbound',
          channel: 'app',
          msg_type: 'text',
          body: text,
          metadata: null,
          created_at_utc: new Date().toISOString(),
          status: 'sending',
          clientMsgId: msgId,
        };
        set((state) => ({ messages: capMessages([localMsg, ...state.messages]), isBotTyping: true }));

        // REST fallback only if WS couldn't send AND is fully disconnected
        // (avoids double-send when WS is mid-reconnect and will flush pending queue)
        if (!sent) {
          api.sendChatMessage(text)
            .then((response) => {
              wsManager.dropPending(msgId);
              set((s) => ({
                messages: prependApiMessages(updateMsgStatus(s.messages, msgId, 'sent'), response.messages || []),
                isBotTyping: false,
              }));
            })
            .catch(() => set((s) => ({ messages: updateMsgStatus(s.messages, msgId, 'failed'), isBotTyping: false })));
        }
      },

      sendButton: (buttonId: string, buttonLabel?: string) => {
        const { sent, msgId } = wsManager.sendButton(buttonId);
        const echoMsg: ChatMessage = {
          id: Date.now() + Math.random(),
          wa_id: '',
          direction: 'inbound',
          channel: 'app',
          msg_type: 'text',
          body: buttonLabel || `[${buttonId}]`,
          metadata: null,
          created_at_utc: new Date().toISOString(),
          status: 'sending',
          clientMsgId: msgId,
        };
        set((state) => ({ messages: capMessages([echoMsg, ...state.messages]) }));

        if (!sent) {
          api.sendButtonPress(buttonId)
            .then((response) => {
              wsManager.dropPending(msgId);
              set((s) => ({ messages: prependApiMessages(updateMsgStatus(s.messages, msgId, 'sent'), response.messages || []) }));
            })
            .catch(() => set((s) => ({ messages: updateMsgStatus(s.messages, msgId, 'failed') })));
        }
      },

      sendListSelect: (itemId: string, itemLabel?: string) => {
        const { sent, msgId } = wsManager.sendListSelect(itemId);
        const echoMsg: ChatMessage = {
          id: Date.now() + Math.random(),
          wa_id: '',
          direction: 'inbound',
          channel: 'app',
          msg_type: 'text',
          body: itemLabel || `[${itemId}]`,
          metadata: null,
          created_at_utc: new Date().toISOString(),
          status: 'sending',
          clientMsgId: msgId,
        };
        set((state) => ({ messages: capMessages([echoMsg, ...state.messages]) }));

        if (!sent) {
          api.sendListSelect(itemId)
            .then((response) => {
              wsManager.dropPending(msgId);
              set((s) => ({ messages: prependApiMessages(updateMsgStatus(s.messages, msgId, 'sent'), response.messages || []) }));
            })
            .catch(() => set((s) => ({ messages: updateMsgStatus(s.messages, msgId, 'failed') })));
        }
      },

      addLocalMessage: (text: string, direction: 'inbound' | 'outbound') => {
        const msg: ChatMessage = {
          id: Date.now(),
          wa_id: '',
          direction,
          channel: 'app',
          msg_type: 'text',
          body: text,
          metadata: null,
          created_at_utc: new Date().toISOString(),
        };
        set((state) => ({ messages: capMessages([msg, ...state.messages]) }));
      },

      deleteMessage: (msgId: string | number) => {
        set((state) => ({
          messages: state.messages.filter((m) => String(m.id) !== String(msgId) && String(m.clientMsgId) !== String(msgId)),
        }));
      },

      clearMessages: () => {
        set({ messages: [], hasMore: false });
      },

      loadMoreHistory: async () => {
        // Chat is local-only — no server fetch. hasMore stays false after clear.
        set({ hasMore: false });
      },

      toggleReaction: (messageId: number, emoji: string) => {
        const { messages } = get();
        const msg = messages.find(m => m.id === messageId);
        if (!msg) return;

        const existing = msg.reactions?.find(r => r.emoji === emoji);
        if (existing) {
          // Remove reaction
          set({
            messages: messages.map(m =>
              m.id === messageId
                ? { ...m, reactions: (m.reactions || []).filter(r => r.emoji !== emoji) }
                : m
            ),
          });
          // Send to server via WS or REST
          const sent = wsManager.send('reaction', { messageId, emoji: null });
          if (!sent) api.removeReaction(messageId).catch(() => {});
        } else {
          // Add reaction (replace any existing from same user)
          const filtered = (msg.reactions || []).filter(r => r.wa_id !== '');
          set({
            messages: messages.map(m =>
              m.id === messageId
                ? { ...m, reactions: [...filtered, { wa_id: '', emoji }] }
                : m
            ),
          });
          const sent = wsManager.send('reaction', { messageId, emoji });
          if (!sent) api.addReaction(messageId, emoji).catch(() => {});
        }
      },
    }),
    {
      name: 'fm-chat-messages',
      storage: createJSONStorage(() => AsyncStorage),
      partialize: (state) => ({ messages: state.messages }),
    },
  ),
);
